import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  Zap,
  Users,
  Shield,
  ArrowRight,
  Clock,
  CreditCard
} from 'lucide-react';

const BillingSetup: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string>('standard');

  const pricingTiers = [
    {
      id: 'basic',
      name: 'Basic',
      price: 200,
      niwts: 3,
      users: 10,
      tokens: '2M',
      features: [
        '3 Niwts',
        '10 Users',
        '2M ChatGPT Tokens/month',
        'Weekly updates',
        'Basic security',
        'Email & chat support'
      ]
    },
    {
      id: 'standard',
      name: 'Standard',
      price: 300,
      niwts: 5,
      users: 25,
      tokens: '5M',
      popular: true,
      features: [
        '5 Niwts',
        '25 Users',
        '5M ChatGPT Tokens/month',
        'Weekly updates',
        'Advanced security',
        'Priority support'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 500,
      niwts: 15,
      users: 50,
      tokens: '10M',
      features: [
        '15 Niwts',
        '50 Users',
        '10M ChatGPT Tokens/month',
        'Daily updates',
        'SSO integration',
        'Phone & chat support',
        'Dedicated account manager'
      ]
    }
  ];

  const handleStartTrial = () => {
    // TODO: Backend - Create trial subscription (no payment)
    // For now, skip directly to Microsoft 365 integration
    navigate('/azure-setup');
  };

  const handlePayNow = () => {
    // TODO: Stripe integration - create checkout session
    // window.location.href = stripeCheckoutUrl;
    alert(`Payment processing will be available soon.\n\nFor now, you can continue with your 14-day free trial.\n\nSelected plan: ${pricingTiers.find(p => p.id === selectedPlan)?.name} - $${pricingTiers.find(p => p.id === selectedPlan)?.price}/month`);
    navigate('/azure-setup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <img
            src="/images/niwt-logo-horizontal.svg"
            alt="Niwt"
            className="h-10 w-auto"
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-6 p-3">
            <img
              src="/images/niwt-logo.png"
              alt="Niwt"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 font-libre mb-3">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Select the plan that fits your team's needs
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium">
            <Zap className="w-4 h-4" />
            14-day free trial included with all plans
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {pricingTiers.map((tier) => (
            <div
              key={tier.id}
              onClick={() => setSelectedPlan(tier.id)}
              className={`relative bg-white rounded-2xl p-6 cursor-pointer transition-all border-2 ${
                selectedPlan === tier.id
                  ? 'border-purple-700 ring-4 ring-purple-100 shadow-lg'
                  : 'border-gray-200 hover:border-purple-300 hover:shadow-md'
              } ${tier.popular ? 'ring-2 ring-blue-200' : ''}`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center px-4 py-1 bg-blue-600 text-white rounded-full text-xs font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              {selectedPlan === tier.id && (
                <div className="absolute top-6 right-6">
                  <div className="w-6 h-6 bg-purple-700 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-gray-900">${tier.price}</span>
                  <span className="text-gray-600 ml-2">/month</span>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className={`w-full px-4 py-2 rounded-lg text-center font-medium transition-colors ${
                selectedPlan === tier.id
                  ? 'bg-purple-700 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {selectedPlan === tier.id ? 'Selected' : 'Select Plan'}
              </div>
            </div>
          ))}
        </div>

        {/* Enterprise Card */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 mb-8 text-white">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex-1">
              <h3 className="text-2xl font-bold mb-2">Enterprise</h3>
              <p className="text-gray-300">
                Unlimited Niwts • Unlimited Users • Custom Security • SAML/SSO • Dedicated Support
              </p>
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="text-3xl font-bold">Custom Pricing</div>
              <button
                onClick={() => window.location.href = 'mailto:sales@niwt.com'}
                className="px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Selected: {pricingTiers.find(p => p.id === selectedPlan)?.name} Plan
            </h3>
            <p className="text-gray-600">
              {formatCurrency(pricingTiers.find(p => p.id === selectedPlan)?.price || 0)}/month
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
            <button
              onClick={handleStartTrial}
              className="flex-1 px-6 py-4 bg-purple-700 text-white rounded-xl hover:bg-purple-800 transition-colors font-semibold flex items-center justify-center gap-2"
            >
              <Clock className="w-5 h-5" />
              Start 14-Day Free Trial
            </button>
            <button
              onClick={handlePayNow}
              className="flex-1 px-6 py-4 border-2 border-purple-700 text-purple-700 rounded-xl hover:bg-purple-50 transition-colors font-semibold flex items-center justify-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Enter Payment Now
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Cancel anytime during the trial period</p>
            <p className="mt-1">Your card will be charged after 14 days</p>
          </div>
        </div>

        {/* Benefits Footer */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">SOC 2 Compliant</h4>
            <p className="text-sm text-gray-600">Enterprise-grade security</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Flexible Team Size</h4>
            <p className="text-sm text-gray-600">Scale up or down as needed</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-3">
              <Zap className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Instant Setup</h4>
            <p className="text-sm text-gray-600">Start using Niwt in minutes</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(amount);
};

export default BillingSetup;
